<template>
  <div class="step5Container">
    <div class="Container">
      <div class="Preview">
        <TabletPreview
          type="ColorSampleMode"
          :order="order"
          :backColor="backColor"
          :frontColor="frontColor"
        />
      </div>

      <div class="Forms">
        <!-- Font Color Selection Area -->
        <section style="margin-bottom: 50px">
          <Heading>彫り/書き/書体</Heading>
          <ButtonGroup>
            <VisualButton
              v-for="item in printTypes"
              :key="item.value"
              :text="item.label"
              :isActive="item.value === form.printType"
              @click="_onClickPrintTypeSelection(item.value)"
            />
          </ButtonGroup>
          <ButtonGroup>
            <template v-for="item in fonts">
              <VisualButton
                v-if="item.show"
                :variant="{
                  smallWidth: item.value === '機械書手彫風書体' ? undefined : 'true',
                  width: item.value === '機械書手彫風書体' ? 'fit' : undefined,
                  align: 'left',
                  type: item.disabled ? 'disabled' : '',
                }"
                :key="item.value"
                :text="item.label"
                :annotation="item.annotation"
                :isActive="item.value === form.font"
                @click="_onClickFontSelection(item.value)"
              />
            </template>
          </ButtonGroup>
          <small>
            注1: レイアウトなし
          </small>
        </section>

        <!-- Font Color Selection Area -->
        <section v-if="form.font">
          <Heading>文字の色</Heading>

          <Heading :variant="{ size: 'h2' }">おもて</Heading>
          <ButtonGroup v-if="form.font">
            <template v-for="item in colorsForFront">
              <VisualButton
                v-if="item.show"
                :variant="{
                  width: 'fit',
                  align: 'left',
                  type: item.disabled ? 'disabled' : '',
                }"
                :key="item.value"
                :text="item.label"
                :isActive="item.disabled ? false : item.value === form.frontColor"
                @click="_onClickFrontColorSelection(item)"
              />
            </template>
          </ButtonGroup>

          <template v-if="!isMindAlte">
            <Heading :variant="{ size: 'h2' }">うら</Heading>
            <ButtonGroup>
              <template v-for="item in colorsForBack">
                <VisualButton
                  v-if="item.show"
                  :variant="{
                    width: 'fit',
                    align: 'left',
                    type: item.disabled ? 'disabled' : '',
                  }"
                  :key="item.value"
                  :text="item.label"
                  :isActive="item.disabled ? false : item.value === form.backColor"
                  @click="_onClickBackColorSelection(item)"
                />
              </template>
            </ButtonGroup>
          </template>
        </section>
      </div>
    </div>

    <OrderFormFooter
      message="彫り・書き/書式を選択してください"
      :canClickGoBack="true"
      @onClickGoBack="onClickGoBack"
      :canClickGoForward="_isSelected()"
      @onClickGoForward="onClickGoForward"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import UsecaseFactory from "@/usecases/usecase.factory.js";
import ProductUsecase from "@/usecases/product.usecase";
import OrderFormUsecase from "@/usecases/orderForm.usecase";

// Components
import OrderFormFooter from "@/components/OrderFormFooter";
import VisualButton from "@/components/VisualButton";
import ButtonGroup from "@/components/ButtonGroup";
import Heading from "@/components/Heading";
import TabletPreview from "@/components/Common/TabletPreview.vue";

import { apiErrorHandler } from "@/services/apiErrorHandler";
export default {
  components: {
    OrderFormFooter,
    Heading,
    VisualButton,
    ButtonGroup,
    TabletPreview,
  },
  data () {
    return {
      productUsecase: new ProductUsecase(),
      orderFormUsecase: new OrderFormUsecase(),
      categories: [],
      printTypes: [],
      fonts: [],
      colorsForFront: [],
      colorsForBack: [],
      canUseColors: [],
      buddhistNameCodes: [],
      canUseFontsAndColorsOfPrintType: {},
      orderDetail: {
        familyEmblem: null,
        sanskritCharacter: null,
        sanskritCharacterBack: null,
        columns: [],
        secularNamePrefix: [],
        diedCharacter: [],
        ageAtDeathPrefix: [],
        ageUnitCharacter: [],
      },
    };
  },
  computed: {
    ...mapState({
      printType: state => state.orderForm.typographyInput.printType,
      clientGroupInput: state => state.orderForm.clientGroupInput,
    }),
    ...mapGetters({
      previousStep: "orderForm/previousStep",
      nextStep: "orderForm/nextStep",
      order: "orderForm/order",
      frontColor: "orderForm/frontColor",
      backColor: "orderForm/backColor",
      favoriteVariantCharacters: "orderForm/getFavoriteVariantCharacters",
      filteredFavoriteVariantCharacters: "orderForm/getFilteredFavoriteVariantCharacters",
      isMindAlte: "orderForm/isMindAlte",
    }),
    form: {
      get () {
        return this.$store.state.orderForm.typographyInput;
      },
      set (value) {
        this.$store.commit("orderForm/setTypographyInput", { ...value });
      },
    },
    detail: {
      get () {
        return this.$store.state.orderForm.detailInput;
      },
    },
    selectedClientGroup: {
      get () {
        return this.$store.state.orderForm.customerInput.clientGroup;
      },
    },
    showSKWriteFont: {
      get () {
        return this.selectedClientGroup.showSKWriteFont;
      },
    },
    showHandCarveWriteFont: {
      get () {
        return this.selectedClientGroup.showHandCarveWriteFont;
      },
    },
    showGoldFilmColor: {
      get () {
        return this.selectedClientGroup.showGoldFilmColor;
      },
    },
  },
  watch: {
    printType: function (val) {
      this.initFontOrLaterSelections();
      this.initFonts(val);
    },
  },
  async created () {
    try {
      await this.fetchFavoriteVariantCharacters();
      const clientGroupId = this.clientGroupInput.id;
      const categories = this.productUsecase.getCategories(clientGroupId);
      this.categories = categories;
      this.printTypes = this.orderFormUsecase.getPrintTypes();
    } catch (err) {
      apiErrorHandler(this, err);
    }
  },
  async mounted () {
    this.$store.commit("orderForm/setCurrentStep", 3);
    if (this.order.detail.productVariation) {
      const productVariationId = this.order.detail.productVariation.id;
      this.buddhistNameCodes = await this.orderFormUsecase.getBuddhistNameCodes(
        productVariationId
      );
    }
    if (this.printType) {
      this.initFonts(this.printType);
    }
  },
  methods: {
    /**
     * [fetchFavoriteVariantCharacters description]
     */
    async fetchFavoriteVariantCharacters () {
      const clientGroupId = this.order.clientGroup.id;

      this.loadingView = this.$loading();

      try {
        const options = { clientGroupId };
        const favoriteVariantCharacters = await favoriteVariantCharactersUsecase.getFavoriteVariantCharacters(options);

        this.$store.commit("orderForm/setFavoriteVariantCharacters", favoriteVariantCharacters);
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.loadingView.close();
      }
    },

    resetItaiji (value) {
      let result = [];
      if (Array.isArray(value) && value.length > 0) {
        result = value.map(o => {
          const obj = {
            variantCharacterId: null,
            character: o.character,
            imageUrl: null,
            points: [],
          };

          // お気に入り異体字登録内に該当する文字があるか探す
          const favoriteVariantCharacter = this.filteredFavoriteVariantCharacters.find(item => {
            return item.character === o.character;
          });

          if (favoriteVariantCharacter) {
            obj.variantCharacterId = favoriteVariantCharacter.variantCharacter.id;
            obj.imageUrl = favoriteVariantCharacter.variantCharacter.imageUrlForTablet;
          }

          return obj;
        });
      }
      return result;
    },
    onClickGoForward () {
      const { $router } = this;
      const { form } = this;
      const params = { ...form };
      this.$store.commit("orderForm/setTypographyInput", params);
      $router.push(this.nextStep);
    },
    onClickGoBack () {
      const { $router } = this;
      $router.push(this.previousStep);
    },
    _onClickPrintTypeSelection (printType) {
      this.form = { ...this.form, printType, font: null };
    },
    _onClickFontSelection (font) {
      this.form = { ...this.form, font, frontColor: null, backColor: null };
      // 初期化
      Object.assign(this.orderDetail, JSON.parse(JSON.stringify(this.detail)));

      // 選択した書体に該当するお気に入り異体字だけを抽出する
      const filteredFavoriteVariantCharacters = this.favoriteVariantCharacters.filter(character => character.font === font);
      this.$store.commit("orderForm/setFilteredFavoriteVariantCharacters", filteredFavoriteVariantCharacters);

      // 異体字を初期化した状態で取り込む
      let columnNumber = 0;
      for (const column of this.orderDetail.columns) {
        // console.log("column:", JSON.stringify(column, null, "\t"));
        // this.orderDetail.columns.push(column);
        this.orderDetail.columns[columnNumber].buddhistName = this.resetItaiji(
          column.buddhistName
        );
        this.orderDetail.columns[columnNumber].secularName = this.resetItaiji(
          column.secularName
        );
        this.orderDetail.columns[columnNumber].dateOfDeath = this.resetItaiji(
          column.dateOfDeath
        );
        this.orderDetail.columns[columnNumber].ageAtDeath = this.resetItaiji(
          column.ageAtDeath
        );
        this.orderDetail.columns[columnNumber].underCharacters = this.resetItaiji(column.underCharacters);
        this.orderDetail.columns[columnNumber].dateOfDeathYear = this.resetItaiji(column.dateOfDeathYear);
        this.orderDetail.columns[columnNumber].dateOfDeathMonth = this.resetItaiji(column.dateOfDeathMonth);
        this.orderDetail.columns[columnNumber].dateOfDeathDay = this.resetItaiji(column.dateOfDeathDay);
        this.orderDetail.columns[columnNumber].ageAtDeathPrefix = this.resetItaiji(column.ageAtDeathPrefix);
        this.orderDetail.columns[columnNumber].ageUnitCharacter = this.resetItaiji(column.ageUnitCharacter);
        columnNumber++;
      }
      this.orderDetail.diedCharacter = this.resetItaiji(
        this.detail.diedCharacter
      );
      this.orderDetail.ageAtDeathPrefix = this.resetItaiji(
        this.detail.ageAtDeathPrefix
      );
      this.orderDetail.ageUnitCharacter = this.resetItaiji(
        this.detail.ageUnitCharacter
      );
      this.orderDetail.secularNamePrefix = this.resetItaiji(
        this.detail.secularNamePrefix
      );
      this.$store.commit("orderForm/setDetail", this.orderDetail);
    },
    _onClickFrontColorSelection (item) {
      const { isMindAlte } = this;
      const frontColor = item.value;
      this.form = { ...this.form, frontColor };

      // マインドアルテの場合は背面がないためここで入力しておく（backColor も必須項目のため）
      if (isMindAlte) {
        this._onClickBackColorSelection(item);
        return;
      }

      // 表の色に対応する compatibleTextColors を参照して裏の文字色の選択可否を制御する
      this._applyCompatibleTextColors(frontColor);
    },
    _onClickBackColorSelection (item) {
      const backColor = item.value;
      this.form = { ...this.form, backColor };
    },
    /**
     * 表の文字色に紐づく compatibleTextColors を元に、裏の文字色の選択可否を制御する。
     * 裏の現在の選択が許可されない組み合わせの場合は、先頭の許可色を強制的に選択する。
     * @param {string} frontColor 表の文字色
     */
    _applyCompatibleTextColors (frontColor) {
      // 表の文字色に該当する戒名コードを探す
      const matched = this.buddhistNameCodes.find(item => {
        return (
          item.printType === this.form.printType &&
          item.font === this.form.font &&
          item.textColor === frontColor
        );
      });
      const compatibleTextColors =
        (matched && matched.compatibleTextColors) || [];

      // 裏の文字色ボタンの活性/非活性を制御する
      // （商品で利用可能な色 かつ 組み合わせが許可された色 のみ活性）
      this.colorsForBack.forEach((color, index) => {
        const availableForProduct = this.canUseColors.includes(color.value);
        const compatible =
          compatibleTextColors.length === 0 ||
          compatibleTextColors.includes(color.value);
        this.colorsForBack[index].disabled = !(
          availableForProduct && compatible
        );
      });

      // 現在選択している裏の色が許可されていない場合は先頭の許可色を強制選択する
      if (
        compatibleTextColors.length &&
        !compatibleTextColors.includes(this.form.backColor)
      ) {
        const nextBackColor =
          compatibleTextColors.find(value =>
            this.canUseColors.includes(value)
          ) || compatibleTextColors[0];
        this._onClickBackColorSelection({ value: nextBackColor });
      }
    },
    _isSelected () {
      const { printType, font, frontColor, backColor } = this.form;
      return !!(printType && font && frontColor && backColor);
    },
    initFontOrLaterSelections () {
      const { printType } = this.form;
      this.$store.commit("orderForm/setTypographyInput", { printType });
    },
    initFonts: function (val) {
      if (!this.order.detail.product) {
        // 商品選択無しの場合、全てを選択できる
        this.fonts = this.orderFormUsecase
          .getFontsAll()
          .map(o => {
            const font = { ...o, disabled: false, show: true };
            if (font.value === "SK書体" && !this.showSKWriteFont) {
              font.show = false;
            }
            if (
              font.value === "機械書手彫風書体" &&
              !this.showHandCarveWriteFont
            ) {
              font.show = false;
            }
            return font;
          });
        this.colorsForFront = this.orderFormUsecase
          .getColorsForFront()
          .map(o => {
            const color = { ...o, disabled: false, show: true };
            if (color.value === "GOLD_FILM" && !this.showGoldFilmColor) {
              color.show = false;
            }
            return color;
          });
        this.colorsForBack = this.orderFormUsecase
          .getColorsForBack()
          .map(o => {
            const color = { ...o, disabled: false, show: true };
            if (color.value === "GOLD_FILM" && !this.showGoldFilmColor) {
              color.show = false;
            }
            return color;
          });
        // 商品選択無しの場合はすべての色を利用可能とする
        this.canUseColors = this.colorsForFront.map(color => color.value);
        return;
      }

      // 全フォントを取得
      let fonts = this.orderFormUsecase.getFontsAll();
      // 書きか彫りか？
      let printType =
        val === this.orderFormUsecase.getWriteString() ? "WRITE" : "CARVE";
      // 戒名コードより使用できるフォントを取り出す
      let canUseFonts = this.buddhistNameCodes.filter(item => {
        return item.printType === printType;
      });
      // 使用できるフォントのボタンをを有効にする
      fonts.forEach((font, index) => {
        let selectFont = canUseFonts.find(item => {
          return item.font === font.value;
        });
        if (selectFont) fonts[index].disabled = false;

        // 得意先の設定に応じて「SK書体」「機械書(手彫風書体)」の表示の切り替えを行う
        font.show = true;
        if (font.value === "SK書体" && !this.showSKWriteFont) {
          font.show = false;
        }
        if (
          font.value === "機械書手彫風書体" &&
          !this.showHandCarveWriteFont
        ) {
          font.show = false;
        }
      });
      // 表裏のすべての文字色をセット
      this.colorsForFront = this.orderFormUsecase.getColorsForFront();
      this.colorsForBack = this.orderFormUsecase.getColorsForBack();
      // 使用できる文字色を取り出す
      let canUseColors = this.buddhistNameCodes
        .filter(item => {
          return item.name === canUseFonts.name && item.printType === printType;
        })
        .map(item => item.textColor);
      // 表の色選択時の組み合わせ制御で利用するため保持しておく
      this.canUseColors = canUseColors;
      // 使用できる文字色（おもて）ボタンを有効にする
      this.colorsForFront.forEach((color, index) => {
        let selectColor = canUseColors.find(item => {
          return item === color.value;
        });
        if (selectColor) this.colorsForFront[index].disabled = false;
        // 得意先の設定に応じて「金箔」の表示の切り替えを行う
        color.show = true;
        if (color.value === "GOLD_FILM" && !this.showGoldFilmColor) {
          color.show = false;
        }
      });
      // 使用できる文字色（うら）ボタンを有効にする
      this.colorsForBack.forEach((color, index) => {
        let selectColor = canUseColors.find(item => {
          return item === color.value;
        });
        if (selectColor) this.colorsForBack[index].disabled = false;
        // 得意先の設定に応じて「金箔」の表示の切り替えを行う
        color.show = true;
        if (color.value === "GOLD_FILM" && !this.showGoldFilmColor) {
          color.show = false;
        }
      });
      this.fonts = fonts;

      // 既に表の色が選択済みの場合（編集時など）は裏の選択可否を反映する
      if (this.form.frontColor && !this.isMindAlte) {
        this._applyCompatibleTextColors(this.form.frontColor);
      }
    },
  },
};

const favoriteVariantCharactersUsecase = UsecaseFactory.create("favoriteVariantCharacters");
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.step5Container {
  margin-bottom: 5%;
}

.Container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 1024px;
  margin: auto;
}

.Preview {
  flex: 1;
}

.Preview-ImageContainer {
  position: relative;
  width: 400px;
  margin: auto;
}

.Preview-ImageContainer .tablet {
  width: 100%;
}

.Forms {
  flex: 1.5;
  overflow: auto;
  height: 520px;
}

.Forms-Inner {
  width: 80%;
  margin: auto;
}

.font {
  position: absolute;
  left: 75px;
  top: 50px;
  width: 40px;
}

.categories::after {
  content: "";
  /* flex: auto; */
}

.card {
  width: 200px;
  height: 250px;
  margin-bottom: 20px;
  cursor: pointer;
}

.card__image {
  width: 100%;
}

.title {
  font-weight: bold;
}

.description {
  color: gray;
}

.isSelected {
  box-shadow: 0 2px 12px 0 $--color-primary !important;
}

.inner {
  display: flex;
}

.leftColumn {
  margin-right: 20px;
}
.leftColumn,
.rightColumn {
  flex: 1;
}
.leftColumn__thumbnail {
  width: 100%;
}

.productNumber {
  background-color: $background-dark-light;
  color: white;
  padding: 3px;
}

.description {
  font-size: 12px;
  color: $text-lighter;
}

.categories {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 80%;
  margin: auto;
}

.categories::after {
  content: "";
  /* flex: auto; */
}

.card {
  width: 400px;
  margin-bottom: 20px;
}

.card__image {
  width: 100%;
}

.title {
  font-weight: bold;
}

.description {
  color: gray;
}
</style>
